<template>
  <div style="position: relative">
    <SelectRoot v-bind="forwarded" v-model="selectedValue">
      <SelectTrigger
        :class="[styles.trigger, { [styles.errorBorder]: error && errorMessage }]"
        :aria-label="placeholder"
        @focusout="handleFocusout"
      >
        <typography
          as="label"
          :for="textareaId"
          v-if="title"
          :variant="isLabelOnTop ? textareaVariants.label : textareaVariants.title"
          :class="[styles.formLabel, { [styles.labelOnTop]: isLabelOnTop }]"
        >
          {{ title }}
        </typography>

        <typography :class="[styles.label, { [styles.filled]: isSelected }]" variant="body-3">
          <SelectValue :placeholder="placeholder" />
        </typography>
        <div :class="styles.chevronContainer">
          <ChevronDownIcon />
        </div>
      </SelectTrigger>

      <SelectPortal :disabled="disablePortal">
        <DropdownSelectContent :class="styles.selectionContent">
          <slot />
        </DropdownSelectContent>
      </SelectPortal>
    </SelectRoot>

    <typography
      as="span"
      :variant="inputVariants.error"
      :class="styles.error"
      v-if="error && errorMessage"
    >
      {{ errorMessage }}
    </typography>
  </div>
</template>

<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core';
import {
  SelectPortal,
  SelectRoot,
  type SelectRootEmits,
  type SelectRootProps,
  SelectTrigger,
  SelectValue,
  useForwardPropsEmits,
  useId,
} from 'radix-vue';

import ChevronDownIcon from 'shared/icons/chevron-down.svg';

import styles from './styles.module.css';
import { DropdownSelectContent } from '../content';
import { type BaseTextareaProps, Typography, type TypographyVariant } from 'src/shared/ui';
import { computed, ref, watch } from 'vue';

export interface DropdownSelectProps extends SelectRootProps {
  disablePortal?: boolean;
  placeholder?: string;
  value?: string;
  title: string;
  size?: 's' | 'm' | 'l';
  error?: boolean;
  errorMessage?: string;
  cb?: (v: any) => void;
}

const props = withDefaults(defineProps<DropdownSelectProps>(), {
  size: 'l',
  value: '',
  cb: () => {},
});

const sizeToInputVariants: Record<
  Required<DropdownSelectProps>['size'],
  { error: TypographyVariant; title: TypographyVariant; label: TypographyVariant }
> = {
  s: { error: 'caption-2', label: 'caption-1', title: 'caption-2' },
  m: { error: 'caption-2', label: 'caption-2', title: 'body-1' },
  l: { error: 'caption-2', label: 'caption-2', title: 'body-3' },
};

const inputVariants = computed(() => {
  return sizeToInputVariants[props.size];
});
const textareaId = useId();
const selectedValue = ref(props.value ?? '');
const isFocus = ref(false);

watch(
  () => props.value,
  (newVal) => {
    const normalized = newVal ?? '';
    if (selectedValue.value !== normalized) {
      selectedValue.value = normalized;
    }
  },
);

const isSelected = computed(() => !!selectedValue.value);
const isLabelOnTop = computed<boolean>((): boolean => {
  return isSelected.value || isFocus.value;
});

const textareaVariants = computed(() => {
  return sizeToTextareaVariants[props.size];
});

const sizeToTextareaVariants: Record<
  Required<BaseTextareaProps>['size'],
  { error: TypographyVariant; title: TypographyVariant; label: TypographyVariant }
> = {
  s: { error: 'caption-2', label: 'caption-1', title: 'caption-2' },
  m: { error: 'caption-2', label: 'caption-2', title: 'body-1' },
  l: { error: 'caption-2', label: 'caption-2', title: 'body-3' },
};

export type DropDownCustomEmits = {
  'update:focusout': [boolean];
};

const emits = defineEmits<SelectRootEmits & DropDownCustomEmits>();
const selectRootProps = reactiveOmit(props, 'disablePortal', 'placeholder');

const forwarded = useForwardPropsEmits(selectRootProps, emits);

watch(selectedValue, (newVal: string) => {
  props.cb(newVal);
});

const handleFocusout = (): void => {
  emits('update:focusout', true);
};
</script>
